/**
 * 成本試算明細純函式：建議價、同料合併、BOM 展開、create／PATCH（整份 lines）body。
 * 表單列：{ key, lineId, mtrId, mtrNo, name, baseUnitName, qty, unitPrice, remark }（qty／unitPrice 為字串）。
 */

const SCALE = 10000;
const MAX_DECIMALS = 4;

let lineKeySeq = 0;

function nextLineKey() {
  lineKeySeq += 1;
  return `new-${Date.now().toString(36)}-${lineKeySeq}`;
}

function isBlank(value) {
  return value === '' || value === null || value === undefined;
}

/** 小數位數（科學記號以實際值計）。 */
export function countDecimals(value) {
  if (isBlank(value)) {
    return 0;
  }
  const number = Number(value);
  if (!Number.isFinite(number)) {
    return 0;
  }
  const text = String(value).includes('e') ? number.toFixed(10) : String(value);
  const fraction = text.split('.')[1] ?? '';
  return fraction.replace(/0+$/, '').length;
}

export function hasAtMostFourDecimals(value) {
  return countDecimals(value) <= MAX_DECIMALS;
}

function toScaled(value) {
  return Math.round(Number(value) * SCALE);
}

function fromScaled(scaled) {
  return String(scaled / SCALE);
}

/** 兩個最多四位小數的數量相加（避免浮點誤差）。 */
export function addQty(left, right) {
  return fromScaled(toScaled(left || 0) + toScaled(right || 0));
}

/**
 * 物料 base_unit_price → 初始建議單價字串：null 為 0；超過四位以 UP 進位至四位。
 */
export function suggestedUnitPrice(baseUnitPrice) {
  if (isBlank(baseUnitPrice)) {
    return '0';
  }
  const number = Number(baseUnitPrice);
  if (!Number.isFinite(number)) {
    return '0';
  }
  if (hasAtMostFourDecimals(number)) {
    return String(number);
  }
  // toFixed(6) 去掉 1.23456*1e4 的浮點尾數後再進位
  return fromScaled(Math.ceil(Number((number * SCALE).toFixed(6))));
}

export function createCostEstimateFormLine({
  lineId = null,
  mtrId,
  mtrNo = '',
  name = '',
  baseUnitName = '',
  qty = '1',
  unitPrice = '',
  remark = '',
}) {
  return {
    key: lineId || nextLineKey(),
    lineId,
    mtrId,
    mtrNo,
    name,
    baseUnitName,
    qty: isBlank(qty) ? '' : String(qty),
    unitPrice: isBlank(unitPrice) ? '' : String(unitPrice),
    remark: remark ?? '',
  };
}

/**
 * 把新加入的物料併入明細：同料只累加數量（保留單價與位置），新料依序附在最後。
 * @param {object[]} lines 目前表單列
 * @param {Array<{ mtrId, mtrNo, name, baseUnitName, qty, unitPrice }>} items
 */
export function mergeItemsIntoLines(lines, items) {
  const next = lines.map((line) => ({ ...line }));
  const indexByMtr = new Map(next.map((line, index) => [line.mtrId, index]));

  items.forEach((item) => {
    if (!item?.mtrId) {
      return;
    }
    const existingIndex = indexByMtr.get(item.mtrId);
    if (existingIndex !== undefined) {
      const existing = next[existingIndex];
      next[existingIndex] = { ...existing, qty: addQty(existing.qty, item.qty) };
      return;
    }
    indexByMtr.set(item.mtrId, next.length);
    next.push(createCostEstimateFormLine(item));
  });

  return next;
}

export function bomGroupSelectionKey(groupDisplayOrder, materialDisplayOrder) {
  return `${groupDisplayOrder}:${materialDisplayOrder}`;
}

/**
 * BOM detail → 要加入試算的物料（同次匯入內同料先加總）。
 * groups[].mtrs[].qty 已是 USED 位置數（NOT_USED 不計）；extra_mtrs[].qty 另計；無套數倍率。
 *
 * @param {object} bom camelCase BOM detail
 * @param {object} options
 * @param {Map<string, object>} options.mtrById 物料 detail（判斷 GROUP、帶單位與建議價）
 * @param {Record<string, string>} options.selections `bomGroupSelectionKey` → 選定的 NORMAL 物料 id
 * @param {Map<string, object>} [options.optionMtrById] GROUP 選項物料摘要（mtrNo／name／baseUnitPrice）
 * @returns {{ items: object[], missingSelections: string[], invalidQty: string[] }}
 */
export function expandBomToItems(bom, { mtrById, selections = {}, optionMtrById = new Map() }) {
  const byMtr = new Map();
  const missingSelections = [];
  const invalidQty = [];

  function describe(mtrId, fallback) {
    const detail = mtrById.get(mtrId) ?? optionMtrById.get(mtrId) ?? {};
    return {
      mtrId,
      mtrNo: detail.mtrNo ?? fallback?.mtrNo ?? '',
      name: detail.name ?? fallback?.name ?? '',
      // 物料 detail 為 unit.base；列表列為 baseUnit
      baseUnitName: detail.unit?.base?.name ?? detail.baseUnit?.name ?? detail.baseUnitName ?? '',
      unitPrice: suggestedUnitPrice(detail.baseUnitPrice),
    };
  }

  function add(mtrId, qty, fallback) {
    if (!hasAtMostFourDecimals(qty)) {
      invalidQty.push(fallback?.mtrNo || mtrId);
      return;
    }
    const existing = byMtr.get(mtrId);
    if (existing) {
      existing.qty = addQty(existing.qty, qty);
      return;
    }
    byMtr.set(mtrId, { ...describe(mtrId, fallback), qty: String(qty) });
  }

  (bom?.groups ?? []).forEach((group) => {
    (group.mtrs ?? []).forEach((material) => {
      const qty = Number(material.qty ?? 0);
      if (!(qty > 0)) {
        return;
      }
      const sourceId = material.mtrId ?? material.mtr?.id;
      const kind = mtrById.get(sourceId)?.mtrKind;
      if (kind === 'GROUP') {
        const key = bomGroupSelectionKey(group.displayOrder, material.displayOrder);
        const selectedId = selections[key];
        if (!selectedId) {
          missingSelections.push(material.mtr?.mtrNo || sourceId);
          return;
        }
        add(selectedId, material.qty, null);
        return;
      }
      add(sourceId, material.qty, material.mtr);
    });
  });

  (bom?.extraMtrs ?? []).forEach((extra) => {
    const qty = Number(extra.qty ?? 0);
    if (!(qty > 0)) {
      return;
    }
    add(extra.mtrId ?? extra.mtr?.id, extra.qty, extra.mtr);
  });

  return { items: [...byMtr.values()], missingSelections, invalidQty };
}

export function mapCostEstimateDetailToFormValues(detail) {
  return {
    name: detail?.name ?? '',
    roundingMode: detail?.roundingMode ?? 'HALF_UP',
    remark: detail?.remark ?? '',
    lines: (detail?.lines ?? []).map((line) => createCostEstimateFormLine({
      lineId: line.lineId,
      mtrId: line.mtr?.id,
      mtrNo: line.mtr?.mtrNo ?? '',
      name: line.mtr?.name ?? '',
      baseUnitName: line.mtr?.baseUnit?.name ?? '',
      qty: line.qty,
      unitPrice: line.unitPrice,
      remark: line.remark ?? '',
    })),
  };
}

function trimOrNull(value) {
  const text = String(value ?? '').trim();
  return text ? text : null;
}

function toNumberOrUndefined(value) {
  return isBlank(value) ? undefined : Number(value);
}

/** 表單列 → API line `{mtrId, qty, unitPrice?, remark?}`；單價空白交後端帶建議價。 */
function toApiLine(line) {
  const item = { mtrId: line.mtrId, qty: Number(line.qty) };
  const unitPrice = toNumberOrUndefined(line.unitPrice);
  if (unitPrice !== undefined) {
    item.unitPrice = unitPrice;
  }
  item.remark = trimOrNull(line.remark);
  return item;
}

/** create body：一次帶完整 lines[]。 */
export function buildCostEstimateCreateBody(values) {
  return {
    name: String(values.name ?? '').trim(),
    roundingMode: values.roundingMode || 'HALF_UP',
    remark: trimOrNull(values.remark),
    lines: (values.lines ?? []).map(toApiLine),
  };
}

/**
 * PATCH body：成本試算只支援 `lines` 整份替換（後端依 mtr_id 同料合併、不收 line_id）。
 * @param {object} values 表單值
 * @param {string} updatedAt detail `record.updatedAt`
 */
export function buildCostEstimatePatchBody(values, updatedAt) {
  return {
    updatedAt,
    ...buildCostEstimateCreateBody(values),
  };
}
